import { useState, useEffect } from 'react';
import { useAuth } from '../../hooks/useAuth';
import { useLanguage } from '../../i18n/LanguageContext';
import { api } from '../../services/api';
import type { GarageMemberResponse } from '../../types/settings';
import EditTeamMembersModal from './EditTeamMembersModal';
import MemberNotificationBadges from './MemberNotificationBadges';

export default function TeamMembersTab() {
  const { token } = useAuth();
  const { t } = useLanguage();
  const [members, setMembers] = useState<GarageMemberResponse[]>([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);

  const loadMembers = async () => {
    if (!token) return;
    try {
      const data = await api.getGarageMembers(token);
      setMembers(data);
    } catch {
      setMembers([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadMembers(); }, [token]);

  const roleLabel = (role: string) => {
    switch (role) {
      case 'OWNER': return t('settings.roleOwner');
      case 'MANAGER': return t('settings.roleManager');
      case 'STAFF': return t('settings.roleStaff');
      default: return role;
    }
  };

  const nonOwnerMembers = members.filter(m => m.role !== 'OWNER');

  if (loading) return <div className="settings-tab-content"><p>{t('common.loading')}</p></div>;

  return (
    <div className="settings-tab-content">
      <div className="settings-section-header">
        <div />
        <button className="settings-edit-link" onClick={() => setShowModal(true)}>
          {t('settings.editAddMembers')} ✏️
        </button>
      </div>

      {nonOwnerMembers.length === 0 ? (
        <div className="settings-empty-state">
          <p>{t('settings.noMembers')}</p>
          <button className="settings-btn-primary" onClick={() => setShowModal(true)}>
            {t('settings.addMember')}
          </button>
        </div>
      ) : (
        <div className="settings-members-list">
          {members.filter(m => m.active !== false).map(member => (
            <div key={member.id} className={`settings-member-row ${!member.active ? 'member-inactive' : ''}`}>
              <div className="member-info">
                <span className="member-name">{member.name || '—'}</span>
                <div className="member-contact">
                  <span>{member.email || '—'}</span>
                  <span>{member.phone || '—'}</span>
                </div>
              </div>
              <div className="member-side">
                <span className="member-role">{roleLabel(member.role)}</span>
                <span className="member-notify-summary">
                  <span className="member-notify-summary-label">{t('settings.memberNotifications.title')}:</span>
                  <MemberNotificationBadges member={member} t={t} />
                </span>
              </div>
            </div>
          ))}
        </div>
      )}

      {showModal && (
        <EditTeamMembersModal
          members={members}
          onClose={() => setShowModal(false)}
          onSaved={() => { setShowModal(false); loadMembers(); }}
          onMemberUpdated={updated => setMembers(prev => prev.map(m => (m.id === updated.id ? updated : m)))}
        />
      )}
    </div>
  );
}
